import React, { useEffect } from 'react';
import { Spin, Card } from 'antd';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import AppLayout from '../../components/Layouts/Layout';
import BackButton from '../../components/BackButton';
import HostOnboardingWizard from '../../components/HostOnboardingWizard';
import { useHostProfile } from '../../contextProviders/HostProfileProvider';
import { TRANSLATIONS } from '../../constants/translations';
import { translate } from '../../helpers/translationHelper';

/**
 * The one and only place the onboarding wizard is ever shown — always a full
 * page, never a modal (there used to be two separate implementations, one
 * inline in HeavyDutyMachineryForm and one in a Modal from MyHeavyDutyMachineries; both
 * are gone now). Reached exclusively via the header's "List your space"
 * button when the user has no HostProfile yet (see AppHeader).
 */
const HostOnboardingPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { hasHostProfile, loading, refresh } = useHostProfile();

  useEffect(() => {
    // Already a host -- nothing to onboard, and the wizard must never be
    // shown again once a HostProfile exists.
    if (!loading && hasHostProfile) navigate('/heavy-duty-machinery/new', { replace: true });
  }, [loading, hasHostProfile, navigate]);

  if (loading || hasHostProfile) {
    return (
      <AppLayout>
        <div style={{ textAlign: 'center', padding: 48 }}>
          <Spin size="large" />
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      <div style={{ width: '100%', maxWidth: 700 }}>
        <div style={{ marginBottom: 8 }}>
          <BackButton fallbackPath="/heavy-duty-machinery" />
        </div>
        <h2 style={{ margin: '0 0 8px' }}>{translate(t, TRANSLATIONS.HOST_ONBOARDING_TITLE)}</h2>
        <p style={{ marginTop: 0, marginBottom: 20, color: 'rgba(0,0,0,0.55)' }}>
          {translate(t, TRANSLATIONS.HOST_ONBOARDING_INTRO)}
        </p>
        <Card style={{ borderRadius: 20, border: '0.5px solid rgba(0,0,0,0.08)', boxShadow: '0 8px 24px rgba(0,0,0,0.06)' }}>
          <HostOnboardingWizard
            onComplete={async () => {
              await refresh();
              navigate('/heavy-duty-machinery/new');
            }}
            onCancel={() => navigate('/heavy-duty-machinery')}
          />
        </Card>
      </div>
    </AppLayout>
  );
};

export default HostOnboardingPage;
